import { PlusOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Button, Form, Input, FormInstance } from 'antd';
import { FormProps } from 'antd/es/form';
import { StoreScopesModal, StoreScopesPreview } from 'components/StoreScopes';
import { LinkButton } from 'components/styled/buttons';
import FlexStyled from 'components/styled/flex';
import useModal from 'hooks/useModal';
import { useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { listDepartments } from 'services/api/department/department';

const FormItem = Form.Item;
interface DepartmentDetail extends StoreHomeV2.DepartmentDetail {}

export interface DepartmentInfoFormValues
  extends Pick<DepartmentDetail, 'platformStoreScopes' | 'name'> {
  id?: DepartmentDetail['id'];
}
export interface DepartmentInfoFormProps extends FormProps<DepartmentInfoFormValues> {
  isDefault?: boolean;
}

const DepartmentInfoForm = (props: DepartmentInfoFormProps) => {
  const { initialValues, isDefault } = props;
  const { t } = useTranslate();
  const storeScopeModal = useModal<{
    value: DepartmentInfoFormValues['platformStoreScopes'];
  }>();
  const formRef = useRef<FormInstance<any>>(null);
  return (
    <>
      <Form<DepartmentInfoFormValues>
        layout="vertical"
        initialValues={initialValues}
        {...props}
        ref={formRef}
      >
        <FormItem hidden name="id" />
        <FormItem
          name="name"
          label={t('department.部门名称')}
          required
          validateTrigger="onSubmit"
          normalize={(v) => v?.trim?.() ?? ''}
          rules={[
            {
              required: true,
              validator: async (_, value) => {
                if (!value) {
                  throw new Error(t('department.请输入x', { x: t('department.部门名称') }));
                }
                const departments = await listDepartments()
                  .then((r) => r.data.data.departments)
                  .catch(() => {
                    return Promise.resolve([]);
                  });
                if (departments.find((t) => t.id !== initialValues?.id && t.name === value)) {
                  throw new Error(t('department.已存在相同名字的部门'));
                }
              },
            },
          ]}
        >
          <Input placeholder={t('department.请输入x', { x: '' })} />
        </FormItem>
        <FormItem noStyle shouldUpdate>
          {(form) => {
            const platformStoreScopes: DepartmentInfoFormValues['platformStoreScopes'] =
              form.getFieldValue('platformStoreScopes');
            const hasValue = platformStoreScopes?.length > 0;
            const openStoreScopeModal = () => {
              storeScopeModal.show({ value: platformStoreScopes ?? [] });
            };
            return (
              <>
                <FormItem
                  className={css`
                    > .ant-form-item-label {
                      label {
                        width: 100%;
                      }
                    }
                  `}
                  label={
                    <FlexStyled.SpaceBetween>
                      {t('department.负责店铺')}
                      {hasValue && !isDefault ? (
                        <LinkButton onClick={openStoreScopeModal} type="link">
                          {t('department.选择x', {
                            x: t('department.负责店铺'),
                          })}
                        </LinkButton>
                      ) : null}
                    </FlexStyled.SpaceBetween>
                  }
                  name="platformStoreScopes"
                >
                  {hasValue ? (
                    // 多加一层，防止内部搜索组件被劫持
                    <>
                      <StoreScopesPreview value={platformStoreScopes} />
                    </>
                  ) : (
                    <>
                      {!isDefault ? (
                        <Button
                          onClick={openStoreScopeModal}
                          className={css`
                            width: 100%;
                          `}
                          type="dashed"
                        >
                          <PlusOutlined />{' '}
                          {t('department.选择x', {
                            x: t('department.负责店铺'),
                          })}
                        </Button>
                      ) : null}
                    </>
                  )}
                </FormItem>
              </>
            );
          }}
        </FormItem>
      </Form>
      <StoreScopesModal
        value={storeScopeModal.meta?.value || []}
        onChange={(scopes) => {
          if (formRef.current) {
            formRef.current.setFieldsValue({
              platformStoreScopes: scopes,
            });
            formRef.current.validateFields(['platformStoreScopes']);
          }
        }}
        visible={storeScopeModal.visible}
        onCancel={() => storeScopeModal.hide()}
      />
    </>
  );
};
export default DepartmentInfoForm;
